import { SuggestionDisclosure } from './suggestion-disclosure.tsx';
import { messageDraftSendIdentity } from '../state/message-draft-store.ts';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, messagesApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { offlineSessionIdentity } from '../state/offline-cache.ts';
import {
  clearMessageDraft,
  clearAllMessageDrafts,
  configureMessageDraftScope,
  readMessageDraft,
  writeMessageDraft,
  type MessageDraft,
} from '../state/message-draft-store.ts';
import { usePlanFeature } from './plan-gate.tsx';
import DemoLimitNote, { useIsPublicDemo } from './demo-limit-note.tsx';
import { useAiDisclosure } from './ai-disclosure.tsx';
import { Alert, Button, LoadingBlock, TextArea, TextInput } from './ui.tsx';
import { isSamePhoneNumber } from '@shared/phone.ts';
import { estimateSmsSegments, smsSenderLabel } from '@shared/sms-segments.ts';
import {
  CUSTOMER_MESSAGE_BODY_MAX,
  CUSTOMER_MESSAGE_SUBJECT_MAX,
  INTERNAL_NOTE_MAX,
  SMS_CUSTOMER_MESSAGE_MAX,
  SMS_CUSTOMER_MESSAGE_MAX_SEGMENTS,
} from '@shared/constants/index.ts';

/**
 * Saying something to a customer, from wherever you happen to be looking at
 * them.
 *
 * The app has always been able to send the messages it composes itself —
 * reminders, quotes, receipts — and never the ones a person needs to send: "we
 * are running an hour late", "the side gate was locked", "thank you, see you
 * next month". Those went out of somebody's own phone, which is why the
 * customer's history in this app has always had holes in it exactly where the
 * conversation was.
 *
 * Three destinations, one box, because the difference between them is a
 * question about the reader rather than about the writing: does this go to
 * their inbox, to their phone, or to nobody outside this company. Email leads,
 * and is the default, because it costs nothing and needs nothing beyond an
 * address; a text is the deliberate choice, and the reasons it might be
 * refused are on screen before the button is pressed rather than after.
 */

type Channel = 'email' | 'sms' | 'internal';

/**
 * What the thread suggests this message should be.
 *
 * The composer used to open the same way every time — blank, on Email —
 * whatever had just been said. Answering a text meant noticing the channel was
 * wrong and changing it, and answering an email meant retyping the subject, so
 * the customer's client filed the answer as a new conversation. Both are
 * things the app already knew and was not saying.
 */
export interface ReplyContext {
  /** The channel the customer last used. */
  channel: 'email' | 'sms';
  /** Their subject, for an email, so the reply threads under it. */
  subject: string | null;
  /**
   * The address they wrote from, where that is not the one on the record — a
   * visit's site contact, or a second address of their own. Filled into the
   * override box so the answer reaches whoever is actually waiting for it.
   */
  replyToAddress?: string | null;
  /**
   * Bumped when somebody presses Reply on a particular message.
   *
   * A counter rather than a boolean: pressing Reply twice on the same message
   * has to re-apply it, and pressing it on a different one has to override
   * whatever was suggested — neither of which a flag can express.
   */
  nonce: number;
  /**
   * Text to start the reply with — a voicemail's suggested reply. Applied
   * with the nonce, and only into an empty box: what is half-typed is theirs.
   */
  body?: string | null;
}

/**
 * What the box addresses itself to, before anybody types over it.
 *
 * The address the message being answered actually came from, which is not
 * always the one on the customer's record: a visit's site contact, or a
 * customer writing in from somewhere else. Empty on a text, where the number
 * on the record is the one that was texted, and empty with nothing to answer
 * — the field then falls back to the record as it always has.
 *
 * Treated exactly like the suggested subject below: filled in for you, and
 * not counted as a draft until somebody changes it.
 */
function suggestedRecipient(context: ReplyContext | undefined): string {
  return context?.channel === 'email' ? (context.replyToAddress ?? '') : '';
}

/** "Re:" once, however many times a thread has been round. */
function replySubject(subject: string | null): string {
  const trimmed = subject?.trim();
  if (!trimmed) return '';
  return /^re:/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
}

interface MessageComposerProps {
  customerId?: string;
  conversationId?: string;
  replyContext?: ReplyContext;
  onSent?: () => void;
  /** Compact addressing disclosure used by the Messages inbox. */
  compactDelivery?: boolean;
}

export function MessageComposer(props: MessageComposerProps) {
  const { t } = useTranslation();
  const session = useSession();
  const hasMessaging = usePlanFeature('messaging');
  const identity = JSON.stringify([
    offlineSessionIdentity(),
    session.user?.id,
    session.user?.role,
    session.user?.permissions,
    session.company?.id,
    session.company?.accessLocked,
    session.company?.effectivePlan,
    session.company?.planFeatures,
    session.currentLocation?.id,
    session.impersonatedBy,
    session.can('customers:write'),
    session.can('customers:read'),
  ]);
  const destination = JSON.stringify([props.customerId, props.conversationId]);
  const scope = useRef({ identity, destination, epoch: 0, blockedReply: null as string | null });
  configureMessageDraftScope(identity);
  if (scope.current.identity !== identity) {
    clearAllMessageDrafts();
    scope.current = {
      identity,
      destination,
      epoch: scope.current.epoch + 1,
      blockedReply: JSON.stringify(props.replyContext) ?? null,
    };
  } else if (scope.current.destination !== destination) {
    scope.current = { ...scope.current, destination, blockedReply: null };
  }
  // Replace the captured editor synchronously on a scope boundary. The new
  // editor fetches scoped customer data and does not inherit the old reply
  // address/subject. Switching back cannot resurrect old component state.
  if (
    session.company?.accessLocked ||
    session.user?.role === 'crew' ||
    !session.can('customers:write') ||
    !session.can('customers:read') ||
    !hasMessaging
  )
    return null;
  const captured = scope.current;
  return (
    <>
      {/* The shared public demo files a send without delivering it
        (simulateDemoSend in routes/messages.ts), so it says so up front. */}
      <DemoLimitNote className="mb-3">{t('publicDemo.composerNote')}</DemoLimitNote>
      <ScopedMessageComposer
        key={`${destination}:${identity}:${captured.epoch}`}
        {...props}
        replyContext={JSON.stringify(props.replyContext) === captured.blockedReply ? undefined : props.replyContext}
        scopeKey={`${identity}:${captured.epoch}`}
        scopeCurrent={() => scope.current === captured && offlineSessionIdentity() === JSON.parse(identity)[0]}
      />
    </>
  );
}

function ScopedMessageComposer({
  customerId,
  /**
   * The thread, when that is what the caller has.
   *
   * The inbox works in conversations and one of them may have no customer at
   * all — a text from a number nobody recognizes. That case can only be
   * texted, and only back to the number that wrote in, so the composer offers
   * exactly that and lets the server say the rest.
   */
  conversationId,
  /** The thread being answered, where the caller is showing one. */
  replyContext,
  /** Called after anything is filed, so a timeline around this can refetch. */
  onSent,
  compactDelivery = false,
  scopeCurrent,
  scopeKey,
}: MessageComposerProps & { scopeCurrent: () => boolean; scopeKey: string }) {
  const { t } = useTranslation();
  const { can, company, user, currentLocation, impersonatedBy } = useSession();
  const authorization = JSON.stringify([
    user?.id,
    user?.role,
    [...(user?.permissions ?? [])].sort(),
    currentLocation?.id,
    company?.accessLocked ?? false,
    can('customers:write'),
    impersonatedBy,
  ]);
  /**
   * Texting is a plan feature, and the credentials outlive the plan.
   *
   * A company that downgrades from Pro keeps its connected Twilio account and
   * its "send text messages" setting, so the two company flags below both stay
   * true while `assertCustomerIsTextable` refuses every send with
   * `PLAN_REQUIRED`. Without this the Starter composer would offer a Text tab
   * whose only possible outcome is a refusal.
   */
  const hasSms = usePlanFeature('sms');
  /**
   * The shared public demo, where a send is simulated rather than delivered.
   *
   * A blank box in a demo is a box nobody tries, so it opens with a sample
   * reply already written. The sample is the composer's own words, like the
   * reply subject: it is never filed as a draft, and a thread opened and left
   * leaves nothing behind.
   */
  const isPublicDemo = useIsPublicDemo();
  const demoSample = isPublicDemo ? t('publicDemo.sampleReply') : '';
  const demoSampleRef = useRef(demoSample);
  demoSampleRef.current = demoSample;
  /**
   * The send identity for the untouched sample, which is never a draft and so
   * never gets one from the draft store. Kept here instead, so a retry after a
   * lost response repeats the same `Idempotency-Key` and the server files one
   * reply rather than two. Replaced once a send of it succeeds.
   */
  const demoSampleSendIdentity = useRef(crypto.randomUUID());
  const queryClient = useQueryClient();
  const disclosure = useAiDisclosure();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Who this box is writing to, which is also what a half-typed message
   * belongs to.
   *
   * The customer where there is one, so the same person's draft is the same
   * draft whether it was started from their record or from the inbox, and the
   * conversation otherwise — a text from a number nobody recognizes has no
   * customer, and two of those are still two different people to answer.
   */
  const draftKey = customerId ?? conversationId ?? null;

  // Read once, on the way in. After this the state below is the truth and the
  // store is only written to.
  const [restored] = useState(() => readMessageDraft(draftKey));

  const [channel, setChannel] = useState<Channel>(restored?.channel ?? replyContext?.channel ?? 'email');
  const [draftError, setDraftError] = useState<string | null>(null);
  const [subject, setSubject] = useState(
    () => restored?.subject ?? (replyContext?.channel === 'email' ? replySubject(replyContext.subject) : ''),
  );
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [body, setBody] = useState(restored?.body ?? demoSample);
  const draftContext = useRef({
    draftKey,
    conversationId,
    channel,
    body,
    hasSms,
    companyId: company?.id,
    authorization,
    revision: 0,
  });
  const previousDraftContext = draftContext.current;
  if (
    previousDraftContext.draftKey !== draftKey ||
    previousDraftContext.conversationId !== conversationId ||
    previousDraftContext.channel !== channel ||
    previousDraftContext.body !== body ||
    previousDraftContext.hasSms !== hasSms ||
    previousDraftContext.companyId !== company?.id ||
    previousDraftContext.authorization !== authorization
  ) {
    draftContext.current = {
      draftKey,
      conversationId,
      channel,
      body,
      hasSms,
      companyId: company?.id,
      authorization,
      revision: previousDraftContext.revision + 1,
    };
  }
  const [recipientEmail, setRecipientEmail] = useState(restored?.recipientEmail ?? suggestedRecipient(replyContext));
  /**
   * Every address this box has filled in for itself, for the person it is
   * currently addressed to.
   *
   * "Did somebody type this, or did we?" cannot be one comparison against the
   * current suggestion, because the suggestion arrives late and changes: the
   * inbox hands the composer the PREVIOUS conversation's messages while the
   * newly selected one loads (`placeholderData`), and a customer opened cold
   * has no thread at all for a moment. So the box remembers what it wrote,
   * and anything else in it is somebody's work. Reset when it moves to
   * another person, whose drafts are their own.
   */
  const composerFilled = useRef(new Set([suggestedRecipient(replyContext)]));
  /**
   * What is in the box right now, readable from an effect that must not
   * re-run on every keystroke — the refresh below fires when the THREAD
   * changes, and depending on the field's own value would re-apply the
   * suggestion over somebody mid-word.
   */
  const recipientEmailRef = useRef(recipientEmail);
  recipientEmailRef.current = recipientEmail;
  const [recipientPhone, setRecipientPhone] = useState(restored?.recipientPhone ?? '');
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<{ channel: Channel; recipient: string | null; simulated?: boolean } | null>(
    null,
  );

  /**
   * The customer, asked for by the same key the detail page uses — so on the
   * page where this composer usually lives it costs nothing, and everywhere
   * else it is one read.
   *
   * It is not optional. Whether this person can be texted at all is a fact
   * about their record — a number, live consent on that number, the line type
   * — and a composer that offered "Text" without knowing would be offering a
   * button whose only outcome is a refusal.
   */
  const customer = useQuery({
    queryKey: ['customer', customerId, scopeKey],
    queryFn: () => customerApi.get(customerId as string),
    enabled: Boolean(customerId),
  });

  /**
   * A thread nobody has attached to a customer yet.
   *
   * Everything the composer normally reasons about — an address, live consent
   * on a line, the note tab — is a fact about a customer RECORD. With none,
   * there is exactly one thing that can be done and one place it can go, and
   * pretending otherwise would offer buttons whose only outcome is a refusal.
   */
  const unattached = !customerId && Boolean(conversationId);

  /**
   * Whether texting is worth offering at all, and — separately — why this
   * particular customer cannot be texted.
   *
   * The same split `PortalLinkSection` makes, for the same reason: a company
   * that does no texting anywhere has no use for the tab, while a company that
   * does needs "why can I email this person and not text them?" answered on
   * screen rather than by a control that is silently missing.
   */
  // The demo has no texting account to configure, and its texts go nowhere.
  const companyCanText =
    isPublicDemo || (hasSms && Boolean(company?.smsConfigured) && Boolean(company?.smsRemindersEnabled));

  /**
   * The fallback is COMMITTED, not merely rendered.
   *
   * Deriving it for the render alone left the real selection sitting at
   * `sms` underneath an Email form. If the session then restored texting
   * before the draft was sent — a reconnected Twilio account, a refreshed
   * plan — the form silently flipped back to Text, hid the subject line and
   * sent a message as a text that the sender had watched become an email.
   * Writing the fallback down means the selection only ever moves once, and
   * visibly.
   */
  useEffect(() => {
    if (channel === 'sms' && !companyCanText && !unattached) setChannel('email');
  }, [channel, companyCanText, unattached]);

  // A thread with no customer has one channel; open on it rather than on the
  // Email the initial state assumes.
  useEffect(() => {
    if (unattached) setChannel('sms');
  }, [unattached]);

  /**
   * Everything typed belongs to the customer on screen, and this composer
   * outlives them: the detail page stays mounted when the customer around it
   * changes, and the calendar's panel is reused for the next visit clicked.
   * Without this, a half-typed message would be sitting in the box addressed
   * to somebody else — and this box sends.
   */
  // Read through a ref so the reset below can restore the CURRENT suggestion
  // without re-running every time the thread refetches and hands over a new
  // object with the same meaning.
  const suggestionRef = useRef(replyContext);
  suggestionRef.current = replyContext;

  /**
   * A different person to write to.
   *
   * On the DRAFT KEY rather than on `customerId`, which is what this watched
   * before and which is `undefined` for every unattached thread alike — so
   * moving between two texts from two unrecognized numbers left the first
   * one's half-typed answer sitting in the box addressed to the second.
   *
   * What it resets TO is whatever was left for this key, falling back to what
   * the thread suggests — not to blank. A customer who last texted should
   * still open on Text after a switch, exactly as they do on first render;
   * resetting to Email here was what made the suggestion look like it was
   * being ignored.
   */
  const settledKey = useRef(draftKey);
  useEffect(() => {
    // The mount run has nothing to do: the state above was initialized from
    // this same key, and re-applying it here would only overwrite it.
    if (settledKey.current === draftKey) return;
    settledKey.current = draftKey;
    const carried = readMessageDraft(draftKey);
    const suggestion = suggestionRef.current;
    setChannel(carried?.channel ?? suggestion?.channel ?? 'email');
    setSubject(carried?.subject ?? (suggestion?.channel === 'email' ? replySubject(suggestion.subject) : ''));
    setBody(carried?.body ?? demoSampleRef.current);
    composerFilled.current = new Set([suggestedRecipient(suggestion)]);
    setRecipientEmail(carried?.recipientEmail ?? suggestedRecipient(suggestion));
    setRecipientPhone(carried?.recipientPhone ?? '');
    setError(null);
    setSentTo(null);
  }, [draftKey]);

  /**
   * The thread arriving, or changing, under a box that is already open.
   *
   * Both paths get this wrong without it. Switching conversations in the
   * inbox leaves the previous thread's suggestion in place while the new one
   * loads, so the box would go on offering the last customer's sender — an
   * answer addressed to the wrong person entirely. Opening a customer cold
   * mounts the composer before their thread exists at all, so it would keep
   * the empty box and fall back to the billing address even where the person
   * who wrote is somebody else.
   *
   * Only what the composer itself put there is replaced. A typed address is
   * somebody's decision about this message and survives the thread arriving
   * underneath it.
   */
  const suggestedTo = suggestedRecipient(replyContext);
  useEffect(() => {
    if (!suggestedTo) return;
    const written = recipientEmailRef.current.trim();
    composerFilled.current.add(suggestedTo);
    if (written && !composerFilled.current.has(written)) return;
    setRecipientEmail(suggestedTo);
  }, [suggestedTo]);

  /**
   * And the other direction: what is in the box, kept where a remount cannot
   * reach it.
   *
   * Written on every change rather than on the way out, because there is no
   * way out to hook: a composer that vanishes because its parent moved is
   * simply gone, with no unmount handler that runs late enough to be sure of
   * what was in it.
   *
   * A draft is what a PERSON put in the box, and the test for that is asked of
   * what is in it NOW rather than of what was ever typed. The composer fills
   * itself in — the reply's subject above all — so anything still equal to
   * what it would have written for itself does not count, and a thread merely
   * READ files nothing. A message deleted again leaves nothing behind either,
   * even though its author had certainly been typing a moment before: a
   * remembered "somebody touched this once" would keep the auto-filled subject
   * alive as a draft that beats the next suggestion.
   *
   * Once something does count, all of it is kept — the message, the channel it
   * is going down, the subject, the address typed over the one on the record —
   * because those belong to the message being written.
   */
  const savedKey = useRef(draftKey);
  useEffect(() => {
    if (!draftKey) return;
    // The pass straight after a key change is still carrying the PREVIOUS
    // recipient's words: the reset above has queued the new ones, but queued
    // is not rendered, so writing here would file Jane's sentence under Sam's
    // name. The pass after this one corrects it — which would be the end of
    // the matter if the store were unbounded. It is not: the transient entry
    // is a twenty-first, and making room for a twenty-first means dropping
    // somebody's real draft, which the correction does not give back.
    if (savedKey.current !== draftKey) {
      savedKey.current = draftKey;
      return;
    }
    const draft: MessageDraft = { channel, subject, body, recipientEmail, recipientPhone };
    // What this box would hold if nobody had touched it — the subject it
    // suggests, and the address the message being answered came from. Only a
    // recipient somebody CHANGED counts as a draft.
    const suggestion = suggestionRef.current;
    const suggested = suggestion?.channel === 'email' ? replySubject(suggestion.subject).trim() : '';
    const written =
      Boolean(body.trim() && body !== demoSampleRef.current) ||
      Boolean(recipientEmail.trim() && !composerFilled.current.has(recipientEmail.trim())) ||
      Boolean(recipientPhone.trim()) ||
      Boolean(subject.trim() && subject.trim() !== suggested);
    if (written) writeMessageDraft(draftKey, draft);
    else clearMessageDraft(draftKey);
  }, [draftKey, channel, subject, body, recipientEmail, recipientPhone]);

  /**
   * Answering, rather than starting something.
   *
   * Runs on the nonce alone — deliberately not on the channel or subject —
   * because those change whenever the thread refetches, and re-applying them
   * mid-sentence would move the channel out from under somebody who had
   * already chosen otherwise. This fires when a person asks for it.
   *
   * The body is only filled when the reply carries one (a voicemail's suggested
   * reply) and the box is empty: whatever is half-typed is theirs.
   */
  const replyNonce = replyContext?.nonce;
  useEffect(() => {
    // Nonce zero is the STANDING suggestion, which the initial state has
    // already applied. Acting on it here would scroll the page to the composer
    // every time somebody merely opened a customer, which is not what opening
    // a customer is for. Only an explicit Reply moves anything.
    if (!replyNonce) return;
    const next = replyContext?.channel ?? 'email';
    setChannel(next);
    setSubject(next === 'email' ? replySubject(replyContext?.subject ?? null) : '');
    const answering = suggestedRecipient(replyContext);
    composerFilled.current.add(answering);
    setRecipientEmail(answering);
    setSentTo(null);
    const proposed = replyContext?.body;
    if (proposed) setBody((current) => (current.trim() ? current : proposed));
    bodyRef.current?.focus();
    // The composer sits under the thread, so on a long one it is off screen.
    // Feature-checked rather than assumed: not every environment this renders
    // in implements it, and a missing convenience must not take the composer
    // down with it.
    bodyRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyNonce]);

  /** The timeline around this has a new row in it either way. */
  const refreshTimeline = async () => {
    await queryClient.invalidateQueries({ queryKey: ['conversations'] });
    await queryClient.invalidateQueries({ queryKey: ['conversation-thread'] });
    onSent?.();
  };

  /**
   * The send takes what was on screen when the button was pressed, and the
   * handler is told the same thing.
   *
   * Nothing is disabled while a send is in flight — on a slow connection
   * somebody may well switch channel or start the next message — so reading
   * `channel` and `body` again in `onSuccess` would announce the wrong channel
   * and wipe edits made in the meantime. The submitted values travel with the
   * mutation instead, and the boxes are cleared only if they still hold
   * exactly what went.
   */
  const send = useMutation({
    mutationFn: async (submitted: {
      customerId?: string;
      conversationId?: string;
      /** Whose draft this was when Send was pressed, which may not be whose it is now. */
      draftKey: string | null;
      sendIdentity: string;
      channel: Channel;
      subject: string;
      body: string;
      /** The rest of the draft as it stood, so the one that went can be told from the next one. */
      recipientEmail: string;
      recipientPhone: string;
    }) => {
      if (
        !mounted.current ||
        !scopeCurrent() ||
        submitted.draftKey !== draftKey ||
        submitted.customerId !== customerId ||
        submitted.conversationId !== conversationId
      )
        throw new Error('stale');
      if (submitted.channel === 'internal') {
        // A note is about a customer, and there is no such thing about a
        // thread nobody has claimed — the channel is not offered there.
        // The same identity as a send, so a retry files one note.
        await messagesApi.addNote(
          { customerId: submitted.customerId as string, body: submitted.body },
          submitted.sendIdentity,
        );
        return null;
      }
      return messagesApi.send(
        {
          // Both where both are known: the server checks they agree rather than
          // trusting the pair, and the conversation is what the inbox has.
          ...(submitted.customerId ? { customerId: submitted.customerId } : {}),
          ...(submitted.conversationId ? { conversationId: submitted.conversationId } : {}),
          channel: submitted.channel,
          subject: submitted.channel === 'email' ? submitted.subject || null : null,
          body: submitted.body,
          recipientEmail: submitted.channel === 'email' ? submitted.recipientEmail.trim() || null : null,
          // Never overridden on an unattached thread: there is exactly one
          // number it means anything to answer.
          recipientPhone: submitted.channel === 'sms' && !unattached ? submitted.recipientPhone.trim() || null : null,
        },
        submitted.sendIdentity,
      );
    },
    onSuccess: async (result, submitted) => {
      if (!scopeCurrent()) return;
      /**
       * The message that went stops being a draft, whoever is on screen.
       *
       * Everything below works by calling this component's setters, and this
       * component may be gone: the inbox moves a thread to the other pane
       * when a tablet turns over, and the composer that pressed Send is
       * replaced by one that reads the store on the way in. Left to the
       * setters alone, the replacement would open holding a message that had
       * already been delivered — and the obvious thing to do with a message
       * sitting in a box is send it.
       *
       * Only what actually went, and only while the store still holds exactly
       * that: anything typed since is the next message, and the same rule the
       * boxes below follow.
       */
      const delivered = !result || result.delivered;
      if (delivered && submitted.draftKey) {
        const stored = readMessageDraft(submitted.draftKey);
        // The WHOLE draft, not just its text. A replacement composer may be
        // preparing the same words down the other channel, or to another
        // address — a different message that happens to read alike, and not
        // one this send has any business deleting.
        const isTheOneThatWent =
          stored?.body === submitted.body &&
          stored?.channel === submitted.channel &&
          stored?.subject.trim() === submitted.subject &&
          stored?.recipientEmail === submitted.recipientEmail &&
          stored?.recipientPhone === submitted.recipientPhone;
        if (isTheOneThatWent) clearMessageDraft(submitted.draftKey);
      }

      /**
       * A send that finishes after the composer has moved on belongs to
       * nobody on screen.
       *
       * This component is reused across customers rather than remounted — the
       * detail page stays mounted when the route's id changes, and the
       * calendar's visit panel is reused for the next visit clicked — so an
       * in-flight send for Jane can land after the props say Sam. Writing
       * Jane's banner into Sam's composer would be merely confusing; clearing
       * Sam's draft because it happened to match Jane's text would lose work.
       *
       * The timeline is still refreshed: the message really was filed, just
       * against a customer nobody is looking at.
       */
      if (submitted.customerId !== customerId) {
        await refreshTimeline();
        return;
      }

      // A request that succeeded is not a message that arrived: the send is
      // filed in the timeline either way, so a provider that refused it is
      // news the sender has to act on rather than a silent difference.
      //
      // The box is cleared only when it actually went. A message nobody
      // received is one the sender is about to try again down the other
      // channel, and wiping what they wrote makes them retype it — the
      // timeline has the text, but retyping is not a reasonable ask of
      // somebody who has just been told their apology bounced.
      if (result && !result.delivered) {
        setSentTo(null);
        setError(
          result.failureReason ??
            t(result.unconfirmed ? 'messageComposer.unconfirmed' : 'messageComposer.notDelivered', {
              recipient: result.queuedFor,
            }),
        );
      } else {
        setError(null);
        setSentTo({ channel: submitted.channel, recipient: result?.queuedFor ?? null, simulated: result?.simulated });
        // Only what actually went. Anything typed since is the next message,
        // and clearing it would throw away work nobody asked to discard.
        setBody((current) => (current === submitted.body ? '' : current));
        if (submitted.body === demoSampleRef.current) demoSampleSendIdentity.current = crypto.randomUUID();
        setSubject((current) => (current === submitted.subject ? '' : current));
        // The address typed over the record was for THAT message, and goes out
        // with it — by the same rule as the two above, so a recipient somebody
        // has changed since, for the next message, stays where they put it.
        //
        // What it goes back TO is the address this thread suggests, not blank.
        // Emptying it would quietly re-address the follow-up to the customer
        // record — so a second message to the site contact who wrote in, sent
        // without pressing Reply again, would reach the billing contact
        // instead. The suggestion has not changed, so nothing else restores
        // it.
        const standing = suggestedRecipient(suggestionRef.current);
        composerFilled.current.add(standing);
        setRecipientEmail((current) => (current === submitted.recipientEmail ? standing : current));
        setRecipientPhone((current) => (current === submitted.recipientPhone ? '' : current));
      }
      await refreshTimeline();
    },
    onError: (caught: unknown, submitted) => {
      // Same rule as the success path: a failure belongs to the customer it
      // was sent for, and putting it in front of the next one is a report
      // about a message they know nothing about.
      if (!mounted.current || !scopeCurrent() || submitted.customerId !== customerId) return;
      setError(caught instanceof ApiRequestError ? caught.message : t('messageComposer.error'));
    },
  });

  /**
   * A suggested reply, written into the box.
   *
   * It REPLACES what is there, and only offers itself when the box is empty —
   * overwriting somebody's half-typed sentence with a model's guess is the one
   * thing this must never do. Nothing is sent: this fills a field, and the
   * send button is the same one it always was.
   */
  const draft = useMutation({
    mutationFn: (submitted: {
      conversationId: string;
      channel: 'email' | 'sms';
      revision: number;
      sessionIdentity: string;
    }) => {
      const current = () =>
        mounted.current &&
        scopeCurrent() &&
        submitted.revision === draftContext.current.revision &&
        submitted.sessionIdentity === offlineSessionIdentity() &&
        !draftContext.current.body.trim();
      return disclosure.ensure(true, current).then(() => {
        if (!current()) throw new Error('stale');
        return messagesApi.draftReply(submitted.conversationId, submitted.channel);
      });
    },
    onSuccess: (result, submitted) => {
      // A response belongs to the unchanged empty composer that requested it.
      // Navigation, typing or a withdrawn channel makes that proposal stale.
      if (
        !mounted.current ||
        !scopeCurrent() ||
        submitted.revision !== draftContext.current.revision ||
        submitted.sessionIdentity !== offlineSessionIdentity() ||
        draftContext.current.body.trim()
      )
        return;
      setDraftError(null);
      setBody(result.draft);
    },
    onError: (caught: unknown, submitted) => {
      if (
        !mounted.current ||
        !scopeCurrent() ||
        submitted.revision !== draftContext.current.revision ||
        submitted.sessionIdentity !== offlineSessionIdentity()
      )
        return;
      // Refusals here are ordinary and worth reading: the month's credits are
      // spent, the assists are switched off, the model had a bad minute.
      setDraftError(caught instanceof ApiRequestError ? caught.message : t('messageComposer.draftError'));
    },
  });

  if (!can('customers:write')) return null;
  // A disabled query is "pending" forever, so this has to ask whether there is
  // a customer to wait FOR — otherwise a thread with none renders a loading
  // block that never resolves and can never be answered.
  if (customerId && customer.isLoading) return <LoadingBlock label={t('messageComposer.loading')} />;
  // A customer that was asked for and did not come back is a page that cannot
  // send. A thread that never had one is a different thing entirely, and still
  // has exactly one number to answer.
  if (customerId && !customer.data) return null;
  if (!customerId && !conversationId) return null;

  const record = customer.data ?? null;

  const textRecipient = recipientPhone.trim() || (record?.phone ?? '');
  const textBlockedReason = unattached
    ? // Nothing to say in advance: there is one number, it wrote to this
      // company, and every remaining gate is the server's to apply.
      null
    : !textRecipient
      ? t('messageComposer.textNoNumber')
      : // Consent and the line type protect a real recipient, and a demo text
        // has none: the server files it without sending it.
        isPublicDemo
        ? null
        : !(
              record!.smsConsentStatus === 'opted_in' &&
              isSamePhoneNumber(record!.smsConsentPhone, textRecipient, company?.countryCode ?? 'US')
            )
          ? t('messageComposer.textNoConsent')
          : // Only where the lookup has answered about *this* number. A null means
            // nobody has asked, which is not a reason to refuse — and neither is a
            // verdict about a different number: `phoneLineType` describes the
            // number on the record, so once somebody types an override it is a
            // claim about a line nobody is writing to.
            record!.phoneLineType === 'landline' &&
              isSamePhoneNumber(record!.phone, textRecipient, company?.countryCode ?? 'US')
            ? t('messageComposer.textLandline')
            : null;

  const channels: Channel[] = unattached
    ? // One channel, because there is one number and no address. Not a
      // reduced menu — a single option, so nothing on screen suggests a
      // choice that does not exist.
      ['sms']
    : companyCanText
      ? ['email', 'sms', 'internal']
      : ['email', 'internal'];

  /**
   * The channel actually in force, which is not always the one that was
   * picked.
   *
   * The session can withdraw texting underneath a Text selection already
   * made — the plan lapses, or somebody disconnects the Twilio account in
   * another tab — and reading the raw state after that leaves a channel with
   * no button, whose only gate (`textBlockedReason`) can perfectly well be
   * null. That is a hidden SMS selection with an enabled Send button behind
   * it. Falling back to email is the honest thing to show: the selection
   * visibly moves, with the typed message intact.
   */
  const activeChannel: Channel = channels.includes(channel) ? channel : (channels[0] ?? 'email');

  /**
   * What this text would cost, counted the way the bill is.
   *
   * Assembled with the sender label in front, because that is what actually
   * goes out and a counter that measured only the typed part would promise two
   * segments and be charged for three. The company's own label is used; a
   * branch that overrides it can shift this by a few characters, which is why
   * the server measures again and refuses rather than trusting this number.
   *
   * It is a live count rather than a cap on the box: a message that is one
   * word too long should be shortened by the person who wrote it, not cut by
   * the app in the middle of a sentence.
   */
  // The server's own function, not an approximation of it. It folds the label
  // to GSM-7 before capping, and that fold is the whole difference: one curly
  // apostrophe in "Maria's" left raw here drops the estimate to UCS-2 and can
  // refuse a message the server would have sent without complaint.
  const segments = estimateSmsSegments(
    `${smsSenderLabel(company?.name ?? '', company?.smsSenderLabel ?? null)}: ${body.trim()}`,
  );
  const overSegmentBudget =
    activeChannel === 'sms' && body.trim().length > 0 && segments > SMS_CUSTOMER_MESSAGE_MAX_SEGMENTS;

  const emailRecipient = recipientEmail.trim() || (record?.email ?? '');
  const emailBlockedReason = emailRecipient ? null : t('messageComposer.emailNoAddress');

  const blockedReason =
    activeChannel === 'email' ? emailBlockedReason : activeChannel === 'sms' ? textBlockedReason : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!mounted.current || !scopeCurrent()) return;
    if (body.trim().length === 0 || blockedReason || overSegmentBudget) return;
    send.mutate({
      customerId,
      conversationId,
      draftKey,
      sendIdentity:
        isPublicDemo && body === demoSample
          ? // The channel is part of it: the same sample sent as an email and
            // then as a text is two messages, not a retry of one.
            `${demoSampleSendIdentity.current}:${activeChannel}`
          : messageDraftSendIdentity(draftKey),
      channel: activeChannel,
      subject: subject.trim(),
      body,
      recipientEmail,
      recipientPhone,
    });
  }

  const deliveryFields = (
    <>
      {activeChannel === 'email' ? (
        <>
          <TextInput
            label={t('messageComposer.toLabel')}
            type="email"
            hint={t('messageComposer.toHint')}
            placeholder={record?.email ?? ''}
            value={recipientEmail}
            onChange={(event) => setRecipientEmail(event.target.value)}
          />
          <TextInput
            label={t('messageComposer.subjectLabel')}
            hint={t('messageComposer.subjectHint')}
            maxLength={CUSTOMER_MESSAGE_SUBJECT_MAX}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
        </>
      ) : null}

      {activeChannel === 'sms' ? (
        <TextInput
          label={t('messageComposer.textToLabel')}
          type="tel"
          hint={t('messageComposer.textToHint')}
          placeholder={record?.phone ?? ''}
          value={recipientPhone}
          onChange={(event) => setRecipientPhone(event.target.value)}
        />
      ) : null}
    </>
  );

  // No rule of its own: where this box sits — and so what it needs separating
  // from — is the caller's to decide. Above a newest-first thread it is divided
  // from the history BELOW it, and on the visit panel it is already inside a
  // section that draws its own line.
  return (
    <>
      {disclosure.dialog}
      <form onSubmit={submit}>
        <h3 className="mb-3 font-semibold">{t('messageComposer.heading')}</h3>

        <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label={t('messageComposer.channelAriaLabel')}>
          {channels.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={activeChannel === value}
              className={activeChannel === value ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => {
                setChannel(value);
                setSentTo(null);
                setError(null);
              }}
            >
              {t(`messageComposer.channel.${value}`)}
            </button>
          ))}
        </div>

        {error ? (
          <div className="mb-3">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}

        {sentTo ? (
          <div className="mb-3">
            <Alert tone="success">
              {sentTo.channel === 'internal'
                ? t('messageComposer.noteAdded')
                : sentTo.simulated
                  ? t('publicDemo.simulatedSent', { recipient: sentTo.recipient })
                  : t('messageComposer.sent', { recipient: sentTo.recipient })}
            </Alert>
          </div>
        ) : null}

        <div className="grid max-w-xl gap-1">
          {compactDelivery && activeChannel !== 'internal' ? (
            <details
              className="messages-delivery"
              open={deliveryOpen || Boolean(blockedReason)}
              onToggle={(event) => setDeliveryOpen(event.currentTarget.open)}
              onInvalidCapture={(event) => {
                // Reveal a malformed override before native form validation
                // focuses it. Addressing remains editable and fully mounted.
                event.currentTarget.open = true;
                setDeliveryOpen(true);
              }}
            >
              <summary>
                {t('messageComposer.deliverySummary', {
                  channel: t(`messageComposer.channel.${activeChannel}`),
                  recipient:
                    activeChannel === 'email' ? emailRecipient : textRecipient || t('messagesInbox.unknownSender'),
                })}
              </summary>
              {deliveryFields}
            </details>
          ) : (
            deliveryFields
          )}

          <TextArea
            label={t(`messageComposer.bodyLabel.${activeChannel}`)}
            hint={
              activeChannel === 'sms'
                ? t('messageComposer.segmentHint', { count: segments, max: SMS_CUSTOMER_MESSAGE_MAX_SEGMENTS })
                : activeChannel === 'internal'
                  ? t('messageComposer.noteHint')
                  : t('messageComposer.emailHint')
            }
            rows={activeChannel === 'internal' ? 2 : 4}
            maxLength={
              activeChannel === 'sms'
                ? SMS_CUSTOMER_MESSAGE_MAX
                : activeChannel === 'internal'
                  ? INTERNAL_NOTE_MAX
                  : CUSTOMER_MESSAGE_BODY_MAX
            }
            value={body}
            onChange={(event) => setBody(event.target.value)}
            ref={bodyRef}
          />

          {/* Only for a message going OUT, and only into an empty box. An
            internal note is nobody's reply, and overwriting a half-typed
            sentence with a model's guess is the one thing this must not do. */}
          {conversationId && activeChannel !== 'internal' ? (
            <SuggestionDisclosure
              key={`${draftKey}:${conversationId}:${activeChannel}`}
              title={t('suggestionDisclosure.reply')}
              busy={draft.isPending}
              failed={Boolean(draftError)}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  busy={draft.isPending}
                  disabled={body.trim().length > 0}
                  onClick={() =>
                    draft.mutate({
                      conversationId,
                      channel: activeChannel === 'sms' ? 'sms' : 'email',
                      revision: draftContext.current.revision,
                      sessionIdentity: offlineSessionIdentity(),
                    })
                  }
                >
                  {draft.isPending ? t('messageComposer.draftBusy') : t('messageComposer.draft')}
                </Button>
                <span className="text-xs text-[color:var(--color-muted)]">{t('messageComposer.draftHint')}</span>
              </div>
              {draftError ? (
                <p role="alert" className="mt-1 text-sm text-[color:var(--color-danger)]">
                  {draftError}
                </p>
              ) : null}
            </SuggestionDisclosure>
          ) : null}

          {blockedReason ? <p className="text-sm text-[color:var(--color-muted)]">{blockedReason}</p> : null}
          {overSegmentBudget ? (
            <p className="text-sm text-[color:var(--color-danger)]">
              {t('messageComposer.tooManySegments', { parts: segments, max: SMS_CUSTOMER_MESSAGE_MAX_SEGMENTS })}
            </p>
          ) : null}

          <div>
            <Button
              type="submit"
              variant={activeChannel === 'internal' ? 'secondary' : 'primary'}
              busy={send.isPending}
              disabled={body.trim().length === 0 || Boolean(blockedReason) || overSegmentBudget}
            >
              {t(`messageComposer.submit.${activeChannel}`)}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
